import { Button, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import * as React from "react";

import { LoadingWrapper } from "../../../components/LoadingWrapper";
import { Helpers } from "../../../utils/helpers";
import { fetchTournamentDetail, TournamentBattleLogDetail as TournamentBattleLogDetailModel, TournamentDetailResponse } from "../api";
import { formatTournamentDuration, renderTournamentStatus, renderTournamentValue, TournamentEmptyState, TournamentJsonBlock, TournamentKeyValueGrid, TournamentPageHeader, TournamentSection, TournamentSourceNotice } from "../components";

export const TournamentBattleLogDetail = (props: any) => {
    const battleId = props.match?.params?.battleId;
    const [response, setResponse] = React.useState<TournamentDetailResponse<TournamentBattleLogDetailModel> | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        document.title = Helpers.documentTitle("Tournament Battle Log");
        loadDetail();
    }, [battleId]);

    const loadDetail = () => {
        setLoading(true);
        setError(null);

        fetchTournamentDetail<TournamentBattleLogDetailModel>(`/api/tournaments/battle-logs/${battleId}`, props.activeEnv)
            .then(data => {
                setResponse(data);
            })
            .catch(err => {
                setError(err?.response?.data?.message || err?.message || "Couldn't load tournament battle log detail.");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const item = response?.data.item;
    const battle = item?.battle;
    const result = item?.result;

    return <div className="contentPadding">
        <TournamentPageHeader
            title="Tournament / Battle Logs / Detail"
            action={<Button disableElevation variant="contained" color="primary" onClick={loadDetail}>Refresh</Button>}
        />

        <Paper className="root" elevation={0} style={{ marginTop: 16, padding: 22 }}>
            <TournamentSourceNotice meta={response?.meta} />

            <LoadingWrapper loading={loading} error={error}>
                {!response?.data.found || !item || !battle || !result ? <TournamentEmptyState
                    title="Battle log not found"
                    description={`No tournament battle log is available for ID ${battleId}.`}
                /> : <React.Fragment>
                    <TournamentSection
                        title="Summary"
                        action={renderTournamentStatus(battle.status)}
                    >
                        <TournamentKeyValueGrid
                            items={[
                                { label: "Battle ID", value: battle.id },
                                { label: "Tournament", value: `${renderTournamentValue(battle.tournamentName)} (${renderTournamentValue(battle.tournamentId)})` },
                                { label: "Round", value: renderTournamentValue(battle.round) },
                                { label: "Duration", value: formatTournamentDuration(battle.durationSeconds) },
                                { label: "Created at", value: battle.createdAt ? Helpers.formatDateWithTime(battle.createdAt, true) : "n/a" },
                                { label: "Updated at", value: battle.updatedAt ? Helpers.formatDateWithTime(battle.updatedAt, true) : "n/a" },
                            ]}
                        />
                    </TournamentSection>

                    <TournamentSection title="Participants">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Player</TableCell>
                                    <TableCell>ID</TableCell>
                                    <TableCell>Seed</TableCell>
                                    <TableCell>Rank</TableCell>
                                    <TableCell>Squad</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {item.participants.map(participant => <TableRow key={String(participant.id)}>
                                    <TableCell>{renderTournamentValue(participant.name)}</TableCell>
                                    <TableCell>{renderTournamentValue(participant.id)}</TableCell>
                                    <TableCell>{renderTournamentValue(participant.seed)}</TableCell>
                                    <TableCell>{renderTournamentValue(participant.rank)}</TableCell>
                                    <TableCell>{renderTournamentValue(participant.squad)}</TableCell>
                                </TableRow>)}
                            </TableBody>
                        </Table>
                    </TournamentSection>

                    <TournamentSection title="Result">
                        <TournamentKeyValueGrid
                            items={[
                                { label: "Winner ID", value: renderTournamentValue(result.winnerId) },
                                { label: "Score A", value: renderTournamentValue(result.scoreA) },
                                { label: "Score B", value: renderTournamentValue(result.scoreB) },
                                { label: "Stars", value: renderTournamentValue(result.stars) },
                                { label: "Percent", value: `${renderTournamentValue(result.percent)}%` },
                                { label: "Replay status", value: renderTournamentValue(result.replayStatus) },
                                { label: "Version", value: renderTournamentValue(result.version) },
                                { label: "Stat version", value: renderTournamentValue(result.statVersion) },
                                { label: "Room", value: renderTournamentValue(result.room) },
                                { label: "Started at", value: result.startedAt ? Helpers.formatDateWithTime(result.startedAt, true) : "n/a" },
                                { label: "Ended at", value: result.endedAt ? Helpers.formatDateWithTime(result.endedAt, true) : "n/a" },
                                { label: "Summary", value: renderTournamentValue(result.summary) },
                            ]}
                        />
                    </TournamentSection>

                    <TournamentSection title="Context">
                        <div style={{ marginBottom: 16 }}>
                            <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
                                Layout
                            </Typography>
                            {item.layout ? <React.Fragment>
                                <Typography variant="body1">{item.layout.name}</Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {`${item.layout.id} | ${item.layout.difficulty} | ${item.layout.mapSize}`}
                                </Typography>
                                <Button size="small" style={{ marginTop: 8 }} onClick={() => props.history.push(`/tournament/layouts/${item.layout?.id}`)}>
                                    Open layout
                                </Button>
                            </React.Fragment> : <Typography variant="body2">No linked layout</Typography>}
                        </div>

                        <div>
                            <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
                                Environment
                            </Typography>
                            {item.environment ? <React.Fragment>
                                <Typography variant="body1">{item.environment.name}</Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {`${item.environment.id} | ${item.environment.type} | ${item.environment.version}`}
                                </Typography>
                                <Button size="small" style={{ marginTop: 8 }} onClick={() => props.history.push(`/tournament/environments/${item.environment?.id}`)}>
                                    Open environment
                                </Button>
                            </React.Fragment> : <Typography variant="body2">No linked environment</Typography>}
                        </div>
                    </TournamentSection>

                    <TournamentJsonBlock title="Raw Payload" data={item.rawPayload || {}} />
                </React.Fragment>}
            </LoadingWrapper>
        </Paper>
    </div>;
};
